import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Package, 
  Settings, 
  LogOut, 
  Mail, 
  Phone, 
  MapPin, 
  Building,
  Calendar,
  Euro,
  ChevronRight,
  CheckCircle,
  Clock,
  Truck,
  XCircle,
  AlertCircle,
  Save
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Account = () => {
  const { user, logout, updateUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    streetName: '',
    houseNumber: '',
    zipCode: '',
    city: '',
    country: 'Netherlands',
    customerType: 'private',
    companyName: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    
    // Load user data into form
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        mobile: user.mobile || '',
        streetName: user.streetName || '',
        houseNumber: user.houseNumber || '',
        zipCode: user.zipCode || '',
        city: user.city || '',
        country: user.country || 'Netherlands',
        customerType: user.customerType || 'private',
        companyName: user.companyName || ''
      });
    }
    
    // Fetch orders
    fetchOrders();
  }, [isAuthenticated, user, navigate]);

  const fetchOrders = async () => {
    if (!user?.email) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/orders/user/${encodeURIComponent(user.email)}`);
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      // Clear company name if switching to private
      if (name === 'customerType' && value === 'private') {
        newData.companyName = '';
      }
      return newData;
    });
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    
    try {
      // Send all profile data including customerType and companyName
      const dataToSend = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        mobile: profileData.mobile,
        streetName: profileData.streetName,
        houseNumber: profileData.houseNumber,
        zipCode: profileData.zipCode,
        city: profileData.city,
        country: profileData.country,
        customerType: profileData.customerType,
        companyName: profileData.customerType === 'business' ? profileData.companyName : ''
      };
      
      await updateUser(dataToSend);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      setSaveError(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={16} className="status-icon paid" />;
      case 'pending':
        return <Clock size={16} className="status-icon pending" />;
      case 'shipped':
        return <Truck size={16} className="status-icon shipped" />;
      case 'delivered':
        return <CheckCircle size={16} className="status-icon delivered" />;
      case 'cancelled':
      case 'failed':
        return <XCircle size={16} className="status-icon cancelled" />;
      default:
        return <Clock size={16} className="status-icon" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="account-page">
      <div className="account-container">
        {/* Sidebar */}
        <div className="account-sidebar">
          <div className="account-user-info">
            <div className="account-avatar">
              {user?.firstName?.charAt(0) || 'U'}
            </div>
            <div className="account-user-details">
              <h3>{user?.firstName} {user?.lastName}</h3>
              <p>{user?.email}</p>
              {(profileData.customerType === 'business' && profileData.companyName) && (
                <span className="user-company-badge">
                  <Building size={12} />
                  {profileData.companyName}
                </span>
              )}
            </div>
          </div>
          
          <nav className="account-nav">
            <button 
              className={`account-nav-item ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              <Package size={20} />
              Order History
              <ChevronRight size={16} className="nav-arrow" />
            </button>
            <button 
              className={`account-nav-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <Settings size={20} />
              Account Settings
              <ChevronRight size={16} className="nav-arrow" />
            </button>
            <button 
              className="account-nav-item logout"
              onClick={handleLogout}
            >
              <LogOut size={20} />
              Sign Out
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="account-content">
          {activeTab === 'orders' && (
            <div className="account-section">
              <h2><Package size={24} /> Order History</h2>
              
              {loadingOrders ? (
                <div className="orders-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading your orders...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="orders-empty">
                  <Package size={48} />
                  <h3>No orders yet</h3>
                  <p>When you place orders, they will appear here.</p>
                  <button onClick={() => navigate('/edible-cups')} className="btn-primary">
                    Start Shopping
                  </button>
                </div>
              ) : (
                <div className="orders-list">
                  {orders.map((order) => (
                    <div key={order.id} className="order-card">
                      <div className="order-header">
                        <div className="order-info">
                          <span className="order-id">Order #{order.id?.slice(0, 8).toUpperCase()}</span>
                          <span className="order-date">
                            <Calendar size={14} />
                            {formatDate(order.created_at)}
                          </span>
                        </div>
                        <div className={`order-status ${order.status}`}>
                          {getStatusIcon(order.status)}
                          {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                        </div>
                      </div>
                      
                      <div className="order-items">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="order-item">
                            <span className="item-name">{item.name}</span>
                            <span className="item-quantity">x{item.quantity}</span>
                            <span className="item-price">€{(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="order-footer">
                        <div className="order-total">
                          <Euro size={16} />
                          Total: €{order.pricing?.finalTotal?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="account-section">
              <h2><Settings size={24} /> Account Settings</h2>
              
              {/* Company Info Box - Show when user has business type with company name */}
              {(profileData.customerType === 'business' && profileData.companyName) && (
                <div className="company-info-box">
                  <div className="company-info-icon">
                    <Building size={24} />
                  </div>
                  <div className="company-info-content">
                    <span className="company-info-label">Business Account</span>
                    <span className="company-info-name">{profileData.companyName}</span>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSaveProfile} className="profile-form">
                <div className="form-section">
                  <h3><User size={18} /> Personal Information</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={profileData.firstName}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={profileData.lastName}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">
                        <Mail size={14} /> Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={profileData.email}
                        disabled
                        className="disabled"
                      />
                      <small>Email cannot be changed</small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="mobile">
                        <Phone size={14} /> Mobile
                      </label>
                      <input
                        type="tel"
                        id="mobile"
                        name="mobile"
                        value={profileData.mobile}
                        onChange={handleChange}
                        placeholder="+31 6 1234 5678"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3><MapPin size={18} /> Address</h3>
                  
                  <div className="form-row">
                    <div className="form-group flex-2">
                      <label htmlFor="streetName">Street Name</label>
                      <input
                        type="text"
                        id="streetName"
                        name="streetName"
                        value={profileData.streetName}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="houseNumber">House No.</label>
                      <input
                        type="text"
                        id="houseNumber"
                        name="houseNumber"
                        value={profileData.houseNumber}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="zipCode">Zip Code</label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={profileData.zipCode}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="city">City</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={profileData.city}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="country">Country</label>
                      <select
                        id="country"
                        name="country"
                        value={profileData.country}
                        onChange={handleChange}
                      >
                        <option value="Netherlands">Netherlands</option>
                        <option value="Belgium">Belgium</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3><Building size={18} /> Business Information (Optional)</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="customerType">Customer Type</label>
                      <select
                        id="customerType"
                        name="customerType"
                        value={profileData.customerType}
                        onChange={handleChange}
                      >
                        <option value="private">Private</option>
                        <option value="business">Business</option>
                      </select>
                    </div>
                    {profileData.customerType === 'business' && (
                      <div className="form-group flex-2">
                        <label htmlFor="companyName">Company Name</label>
                        <input
                          type="text"
                          id="companyName"
                          name="companyName"
                          value={profileData.companyName}
                          onChange={handleChange}
                          placeholder="Enter your company name"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {saveError && (
                  <div className="save-message error">
                    <AlertCircle size={16} />
                    {saveError}
                  </div>
                )}
                
                {saveSuccess && (
                  <div className="save-message success">
                    <CheckCircle size={16} />
                    Profile updated successfully!
                  </div>
                )}

                <button type="submit" className="save-profile-btn" disabled={saving}>
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Account;